import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import { initializeApollo } from '@yema/commerce/lib/apollo';
import { useSelector, useDispatch } from '@yema/commerce/StoreClient';
import { initialize } from 'utils/initialize';
import initializeUserContext from 'utils/initializeUserContext';
import { get_ } from 'sf-modules';
import { getLandingSlugs, getLanding } from 'functions/fnRequestAPIs/fnCMSPagesAPI';
import { getMenus } from 'functions/fnRequestAPIs/fnMenus';
import NotFoundLayout from 'layouts/NotFound.layout';
import PageLayout from 'layouts/Page.layout';
import CMS_Page from 'components/cms/CMS_Page';

const Campaign = ({ pageContent, staticContent }) => {
    const router = useRouter();
    const authToken = useSelector(({ authentication }) => authentication.token);
    const dispatch = useDispatch();

    useEffect(() => initializeUserContext({ authToken, dispatch }), [authToken, router.asPath]);

    if (!router.isFallback && (!pageContent || !pageContent.data || pageContent.error))
        return <NotFoundLayout staticContent={staticContent} />;

    return (
        <PageLayout className='page cms-page' staticContent={staticContent}>
            <CMS_Page loading={router.isFallback} content={get_(pageContent, ['data'])} />
        </PageLayout>
    );
};

export async function getStaticProps(context) {
    try {
        if (!context.params.collectionName) return { notFound: true };

        const { reduxStore, apolloClient } = await initialize({
            withReduxStore: true,
            withApolloClient: true,
            withUserContext: false,
        });

        const menus = await getMenus(context, apolloClient);
        const page = await getLanding(apolloClient, context.params.collectionName, context.preview);

        if (!page || !page.data) return { notFound: true };

        const metaTags = {
            title: get_(page, ['data', 'seoTitle']),
            description: get_(page, ['data', 'searchDescription']),
            image: get_(page, ['data', 'seoImage', 'file']),
            shouldIndex: get_(page, ['data', 'seoShouldIndex']),
            canonicalUrl: get_(page, ['data', 'seoCanonicalUrl']),
        };

        return {
            props: {
                initialReduxState: reduxStore.getState(),
                revalidatedOn: Date.now(),
                pageContent: page,
                staticContent: {
                    ...menus,
                    metaTags,
                    preview: !!context.preview,
                },
            },

            // Re-generate the page:
            // - when a request comes in
            // - at most once every X seconds
            revalidate: 120,
        };
    } catch (error) {
        // The API most likely died
        console.error(error);
        return {
            props: {},
        };
    }
}

export async function getStaticPaths() {
    const apolloClient = initializeApollo();
    const allPages = await getLandingSlugs(apolloClient);
    return {
        paths: allPages?.map((slug) => `/cl/${slug}`) || [],
        fallback: 'blocking',
    };
}

export default Campaign;
